import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useSyncStore } from '../stores/syncStore'
import { useGmailStore } from '../stores/gmailStore'
import { driveService } from '../services/googleDrive'
import { Button, Card, ConfirmModal } from '../components/ui'
import { toast } from '../components/ui/Toast'

interface DriveFile {
  name: string
  size: string
  modifiedTime: string
}

export default function Vault() {
  const { user, logout, hasGmailScope, connectGmail, disconnectGmail } = useAuthStore()
  const { isConnected, lastSync, folderId } = useSyncStore()
  const [files, setFiles] = useState<DriveFile[]>([])
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isNukeConfirmOpen, setIsNukeConfirmOpen] = useState(false)
  const [isConnectingGmail, setIsConnectingGmail] = useState(false)
  const [isDisconnectGmailOpen, setIsDisconnectGmailOpen] = useState(false)

  useEffect(() => {
    loadFiles()
  }, [])

  const loadFiles = async () => {
    setIsLoadingFiles(true)
    try {
      const driveFiles = await driveService.listFiles()
      setFiles(driveFiles)
    } catch (error) {
      console.error('Failed to load files:', error)
      toast('Failed to load files', 'error')
    } finally {
      setIsLoadingFiles(false)
    }
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const data = await driveService.exportAllData()
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `praxis-crm-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast('Data exported successfully', 'success')
    } catch (error) {
      console.error('Failed to export:', error)
      toast('Export failed', 'error')
    } finally {
      setIsExporting(false)
    }
  }

  const handleNukeCache = () => {
    sessionStorage.clear()
    localStorage.clear()
    useGmailStore.getState().clearCache()
    toast('Local cache cleared. Drive data untouched.', 'success')
    setIsNukeConfirmOpen(false)
  }

  const handleConnectGmail = async () => {
    setIsConnectingGmail(true)
    try {
      await connectGmail()
      toast('Gmail connected successfully', 'success')
    } catch {
      toast('Failed to connect Gmail', 'error')
    } finally {
      setIsConnectingGmail(false)
    }
  }

  const handleDisconnectGmail = () => {
    disconnectGmail()
    toast('Gmail disconnected', 'info')
    setIsDisconnectGmailOpen(false)
  }

  const formatFileSize = (bytes: string) => {
    const size = parseInt(bytes, 10)
    if (isNaN(size)) return '—'
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-semibold text-text-primary mb-6">Your data</h1>

      <div className="space-y-6">
        <Card>
          <h2 className="text-sm font-semibold text-text-primary mb-4">
            Connection status
          </h2>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">Status</span>
              <span className={isConnected ? 'text-success font-medium' : 'text-error font-medium'}>
                {isConnected ? 'Connected' : 'Offline'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Account</span>
              <span>{user?.email || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Last sync</span>
              <span>{lastSync ? formatDate(lastSync.toISOString()) : 'Never'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Folder ID</span>
              <span className="font-mono text-xs truncate ml-4 max-w-[200px]" title={folderId ?? undefined}>
                {folderId?.slice(0, 20) || '—'}...
              </span>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-sm font-semibold text-text-primary mb-2">
            Gmail integration
          </h2>
          
          <p className="text-sm text-text-secondary mb-4">
            {hasGmailScope
              ? 'Gmail is connected. You can view email history on contact records and compose emails with pre-filled context.'
              : 'Connect Gmail to see email history on contact records and quickly compose contextual follow-up emails.'}
          </p>

          <div className="space-y-3 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-text-secondary">Status</span>
              <span className={hasGmailScope ? 'text-success font-medium' : 'text-text-secondary'}>
                {hasGmailScope ? 'Connected' : 'Not connected'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Access level</span>
              <span>{hasGmailScope ? 'Read-only' : '—'}</span>
            </div>
          </div>

          {hasGmailScope ? (
            <div className="space-y-3">
              <div className="bg-[#EDF5EF] rounded-lg p-3">
                <p className="text-xs text-success">
                  Emails are read directly from Gmail when you view a contact. Nothing is stored in the app.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setIsDisconnectGmailOpen(true)}
              >
                Disconnect Gmail
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-[#F5F5F3] rounded-lg p-3">
                <p className="text-xs text-text-secondary">
                  This grants read-only access to view your emails. The app cannot send, delete, or modify any emails.
                </p>
              </div>
              <Button
                variant="primary"
                onClick={handleConnectGmail}
                disabled={isConnectingGmail}
              >
                {isConnectingGmail ? 'Connecting...' : 'Connect Gmail'}
              </Button>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-text-primary">
              Your data files
            </h2>
            <Button variant="ghost" size="sm" onClick={loadFiles} disabled={isLoadingFiles}>
              {isLoadingFiles ? 'Loading...' : 'Refresh'}
            </Button>
          </div>

          <p className="text-sm text-text-secondary mb-4">
            These files exist in your Google Drive in the /PraxisCRM_Data/ folder.
            We cannot access anything else.
          </p>

          {files.length === 0 ? (
            <p className="text-sm text-text-secondary">
              {isLoadingFiles ? 'Loading...' : 'No files found.'}
            </p>
          ) : (
            <div className="bg-bg-surface border border-border rounded-lg overflow-hidden">
              <table className="table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th className="text-right">Size</th>
                    <th className="text-right">Modified</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr key={file.name}>
                      <td className="font-mono text-sm">{file.name}</td>
                      <td className="text-right text-text-secondary">
                        {formatFileSize(file.size)}
                      </td>
                      <td className="text-right text-text-secondary">
                        {formatDate(file.modifiedTime)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card>
          <h2 className="text-sm font-semibold text-text-primary mb-2">
            Data portability
          </h2>
          
          <p className="text-sm text-text-secondary mb-4">
            Your data belongs to you. Export it anytime, in full, no questions asked.
          </p>

          <Button
            variant="primary"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export all data'}
          </Button>
        </Card>

        <Card>
          <h2 className="text-sm font-semibold text-error mb-4">
            Danger zone
          </h2>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-text-secondary mb-3">
                Clear all local browser data (session, cache). Your Drive data remains untouched.
              </p>
              <Button
                variant="destructive"
                onClick={() => setIsNukeConfirmOpen(true)}
              >
                Clear local cache
              </Button>
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-sm text-text-secondary mb-3">
                Disconnect from Google Drive and log out.
              </p>
              <Button variant="destructive" onClick={logout}>
                Disconnect and log out
              </Button>
            </div>
          </div>
        </Card>

        <div className="text-center pt-6">
          <p className="text-xs text-text-secondary">
            Praxis CRM v1.0.0
          </p>
        </div>
      </div>

      <ConfirmModal
        isOpen={isNukeConfirmOpen}
        onClose={() => setIsNukeConfirmOpen(false)}
        onConfirm={handleNukeCache}
        title="Clear local cache"
        message="This will clear all local browser storage. Your data in Google Drive will remain completely untouched. You may need to re-authenticate."
        confirmText="Clear cache"
        variant="danger"
      />

      <ConfirmModal
        isOpen={isDisconnectGmailOpen}
        onClose={() => setIsDisconnectGmailOpen(false)}
        onConfirm={handleDisconnectGmail}
        title="Disconnect Gmail"
        message="This will disconnect Gmail integration. You'll no longer see email history on contact records. You can reconnect at any time."
        confirmText="Disconnect"
        variant="danger"
      />
    </div>
  )
}
